/**
 * Couche d'analytique minimale et sans dépendance.
 * Prête pour Google Analytics (gtag / dataLayer), Meta Pixel (fbq)
 * et l'API de conversions côté serveur (à brancher plus tard).
 *
 * Aucun appel réseau n'est fait ici : on ne fait que pousser les événements
 * vers les outils déjà chargés sur la page, s'ils sont présents.
 */

export type AnalyticsEvent =
  | "primary_cta_clicked"
  | "lead_form_started"
  | "lead_form_step_completed"
  | "lead_form_submitted"
  | "portfolio_viewed"
  | "case_study_viewed";

type Params = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const trackEvent = (event: AnalyticsEvent, params: Params = {}) => {
  if (typeof window === "undefined") return;

  const payload = { event, ...params, ...getUtmParams() };

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    window.gtag?.("event", event, payload);
    window.fbq?.("trackCustom", event, payload);
  } catch {
    // l'analytique ne doit jamais casser l'expérience
  }
};

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
] as const;

const STORAGE_KEY = "a8020_utm";

/** Capture les paramètres UTM de l'URL et les conserve pour la session. */
export const captureUtmParams = () => {
  if (typeof window === "undefined") return;
  const search = new URLSearchParams(window.location.search);
  const found: Record<string, string> = {};
  UTM_KEYS.forEach((key) => {
    const value = search.get(key);
    if (value) found[key] = value.slice(0, 200);
  });
  if (Object.keys(found).length === 0) return;
  try {
    const existing = getUtmParams();
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...existing, ...found }));
  } catch {
    // stockage indisponible : on ignore
  }
};

/** Retourne les UTM conservés, à joindre aux soumissions de formulaire. */
export const getUtmParams = (): Record<string, string> => {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
};
